/**
 * Font synchronization for iframe popups using CSS inheritance
 *
 */

/**
 * Copies font resources (Google Fonts links, @font-face rules) from parent to iframe
 */
function copyFontResourcesToIframe(iframeDoc: Document): void {
  try {
    // Copy Google Fonts and other font links
    document.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
      const href = link.getAttribute('href');
      if (
        href &&
        (href.match(
          /fonts\.(googleapis|gstatic|adobe)\.com|typekit\.net|fontawesome\.com|font/i,
        ) ||
          href.includes('font') ||
          href.includes('typography'))
      ) {
        const existingLink = iframeDoc.querySelector(`link[href="${href}"]`);
        if (!existingLink) {
          const newLink = document.createElement('link');
          newLink.rel = 'stylesheet';
          newLink.href = href;
          newLink.type = 'text/css';
          iframeDoc.head.appendChild(newLink);
        }
      }
    });

    // Copy @font-face rules from style tags
    document.querySelectorAll('style').forEach((styleTag) => {
      if (styleTag.textContent && styleTag.textContent.includes('@font-face')) {
        const newStyle = document.createElement('style');
        newStyle.type = 'text/css';
        newStyle.textContent = styleTag.textContent;
        iframeDoc.head.appendChild(newStyle);
      }
    });

    // Copy @font-face rules from stylesheets
    try {
      const sheets = Array.from(document.styleSheets);
      sheets.forEach((sheet) => {
        try {
          if (sheet.cssRules) {
            Array.from(sheet.cssRules).forEach((rule) => {
              if (rule instanceof CSSFontFaceRule) {
                const newStyle = document.createElement('style');
                newStyle.type = 'text/css';
                newStyle.textContent = rule.cssText;
                iframeDoc.head.appendChild(newStyle);
              }
            });
          }
        } catch (e) {
          // Ignore cross-origin errors
        }
      });
    } catch (e) {
      // Ignore cross-origin errors
    }

    console.log('CandyRack: Font resources copied to iframe');
  } catch (error) {
    console.warn('CandyRack: Failed to copy font resources to iframe', error);
  }
}

/**
 * Applies theme fonts to iframe using CSS inheritance approach
 * Copies theme CSS variables and font resources into the iframe since iframes are isolated
 */
export function injectFontsIntoIframe(iframe: HTMLIFrameElement): void {
  try {
    const iframeDoc = iframe.contentDocument;
    if (!iframeDoc || !iframeDoc.head) {
      console.warn('CandyRack: Iframe document not accessible');
      return;
    }

    // Remove existing font sync styles
    const existingFontSync = iframeDoc.getElementById('candyrack-font-sync');
    if (existingFontSync) {
      existingFontSync.remove();
    }

    // Copy font resources from parent document to iframe
    copyFontResourcesToIframe(iframeDoc);

    // Get actual computed fonts from parent document
    const parentBody = document.body;
    const bodyComputedStyle = window.getComputedStyle(parentBody);
    const bodyFontFamily = bodyComputedStyle.fontFamily;

    // Get heading font from first heading element
    const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
    let headingFontFamily = '';
    if (headings.length > 0) {
      const headingComputedStyle = window.getComputedStyle(headings[0]);
      headingFontFamily = headingComputedStyle.fontFamily;
    }

    // Also check for CSS custom properties as fallback
    const parentRoot = document.documentElement;
    const rootComputedStyle = window.getComputedStyle(parentRoot);
    const cssVarBodyFont = rootComputedStyle
      .getPropertyValue('--font-body-family')
      .trim();
    const cssVarHeadingFont = rootComputedStyle
      .getPropertyValue('--font-heading-family')
      .trim();

    // Use CSS variables if available, otherwise use computed styles
    const finalBodyFont = cssVarBodyFont || bodyFontFamily;
    const finalHeadingFont = cssVarHeadingFont || headingFontFamily;

    // Apply the same CSS inheritance approach as the extension
    const fontStyleElement = document.createElement('style');
    fontStyleElement.type = 'text/css';
    fontStyleElement.id = 'candyrack-font-sync';

    // Check if custom CSS exists - if so, don't apply font sync to avoid conflicts
    const hasCustomCss = iframeDoc
      .querySelector('#settings-css')
      ?.textContent?.trim();

    let fontCss = `
      html, body {
        font-family: var(--font-body-family, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif) !important;
        background: transparent !important;
      }

      /* Heading elements use heading font variables with proper fallback - only font-family */
      h1, h2, h3, h4, h5, h6 {
        font-family: var(--font-heading-family, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif) !important;
      }

      /* All other elements inherit fonts from their parent */
      .candyrack-popup * {
        font-family: inherit !important;
      }
    `;

    // If custom CSS exists, don't apply font sync styles to avoid conflicts
    if (hasCustomCss) {
      console.log(
        'CandyRack: Custom CSS detected, skipping font sync to avoid conflicts',
      );
      return;
    }

    // Add theme CSS variables to iframe if they exist - only font families
    if (finalBodyFont || finalHeadingFont) {
      let rootVars = ':root {\n';

      if (finalBodyFont) {
        rootVars += `  --font-body-family: ${finalBodyFont};\n`;
      }

      if (finalHeadingFont) {
        rootVars += `  --font-heading-family: ${finalHeadingFont};\n`;
      }

      rootVars += '}\n\n';
      fontCss = rootVars + fontCss;
    }

    fontStyleElement.textContent = fontCss;
    iframeDoc.head.appendChild(fontStyleElement);

    console.log('CandyRack: Font inheritance applied successfully', {
      bodyFont: finalBodyFont || 'inherit',
      headingFont: finalHeadingFont || 'inherit',
      generatedCSS: fontCss,
    });
  } catch (error) {
    console.warn('CandyRack: Failed to apply font inheritance', error);
  }
}

/**
 * Waits for iframe to be ready
 */
function waitForIframeReady(
  iframe: HTMLIFrameElement,
  maxAttempts: number = 10,
): Promise<boolean> {
  return new Promise((resolve) => {
    let attempts = 0;

    const checkIframe = () => {
      attempts++;

      try {
        const iframeDoc = iframe.contentDocument;
        if (iframeDoc && iframeDoc.head && iframeDoc.body) {
          resolve(true);
          return;
        }
      } catch (e) {}

      if (attempts >= maxAttempts) {
        console.warn('CandyRack: Iframe not ready after maximum attempts');
        resolve(false);
        return;
      }

      setTimeout(checkIframe, 100);
    };

    checkIframe();
  });
}

/**
 * Sync fonts with iframe using simple inheritance approach
 */
export function syncFontsWithIframe(iframe: HTMLIFrameElement): void {
  if (window.CANDYRACK_IS_DISABLE_FONT_FAMILY) {
    console.log('CandyRack: Font synchronization disabled by user');
    return;
  }

  waitForIframeReady(iframe).then((isReady) => {
    if (!isReady) {
      console.warn('CandyRack: Iframe not ready for font synchronization');
      return;
    }

    injectFontsIntoIframe(iframe);
  });
}

/**
 * Retry mechanism for font synchronization
 */
export function syncFontsWithRetry(
  iframe: HTMLIFrameElement,
  maxRetries: number = 3,
): void {
  let retryCount = 0;

  const attemptSync = () => {
    try {
      if (document.fonts && document.fonts.status === 'loading') {
        console.log('CandyRack: Fonts still loading, waiting...');
        document.fonts.ready.then(() => {
          syncFontsWithIframe(iframe);
        });
        return;
      }

      syncFontsWithIframe(iframe);
    } catch (error) {
      retryCount++;
      if (retryCount < maxRetries) {
        const delay = Math.pow(2, retryCount) * 200;
        console.log(
          `CandyRack: Font sync failed, retrying in ${delay}ms (attempt ${retryCount}/${maxRetries})`,
        );
        setTimeout(attemptSync, delay);
      } else {
        console.warn(
          'CandyRack: Font synchronization failed after maximum retries',
        );
      }
    }
  };

  attemptSync();
}

declare global {
  interface Window {
    CANDYRACK_IS_DISABLE_FONT_FAMILY?: boolean;
    CANDYRACK_SYNC_FONTS_WITH_RETRY?: (
      iframe: HTMLIFrameElement,
      maxRetries?: number,
    ) => void;
  }
}

if (typeof window !== 'undefined') {
  window.CANDYRACK_SYNC_FONTS_WITH_RETRY = syncFontsWithRetry;
}
